import React, { useState, useEffect } from 'react';

const HistoricalAnalytics = () => {
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    fetchHistoricalData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchHistoricalData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Listen for changes made from other tabs (storage events)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'analytics_local_sessions') {
        // new session recorded in another tab – refresh UI quickly
        fetchHistoricalData();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching historical analytics data...');
      
      // Try the main API endpoint first
      let response = await fetch('/api/analytics', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 API Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received historical data:', data);
        console.log('📊 Recent sessions count:', data.recentSessions?.length || 0);
        setHistoricalData(mergeWithLocalSessions(data));
        setError(null);
        return;
      }

      // If API fails, try the development JSON file
      console.log('⚠️ Main API failed, trying development JSON file...');
      try {
        response = await fetch('/dev-analytics.json');
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Loaded development JSON data:', data);
          setHistoricalData(mergeWithLocalSessions(data));
          setError('Using development data from static file');
          return;
        }
      } catch (jsonError) {
        console.log('⚠️ Development JSON file not available:', jsonError);
      }

      // Final fallback to generated demo data
      console.log('⚠️ All API methods failed, using generated demo data');
      const demoData = generateDemoData();
      console.log('🎭 Generated demo data:', demoData);
      setHistoricalData(mergeWithLocalSessions(demoData));
      setError('Using generated demo data - API and static files unavailable');

    } catch (error) {
      console.error('❌ Error fetching historical data:', error);
      const demoData = generateDemoData();
      console.log('🎭 Fallback to demo data:', demoData);
      setHistoricalData(mergeWithLocalSessions(demoData));
      setError(`Failed to fetch data - ${error.message}. Showing demo data.`);
    } finally {
      setLoading(false);
    }
  };

  // Merge sessions saved locally (when the backend is unreachable) so that the
  // dashboard updates immediately while developing / offline.
  const mergeWithLocalSessions = (data) => {
    try {
      const localSessions = JSON.parse(localStorage.getItem('analytics_local_sessions') || '[]');

      if (localSessions.length === 0) return data;

      const existingIds = new Set(
        (data.recentSessions || []).map((s) => {
          try {
            return JSON.parse(s).sessionId;
          } catch {
            return undefined;
          }
        })
      );

      const dedupedLocal = localSessions.filter((s) => !existingIds.has(s.sessionId));

      const merged = {
        ...data,
        recentSessions: [
          ...(data.recentSessions || []),
          ...dedupedLocal.map((s) => JSON.stringify(s))
        ]
      };

      // Best-effort: increment summary counts if present
      if (merged.summary) {
        merged.summary.totalSessions =
          (parseInt(merged.summary.totalSessions || 0) + dedupedLocal.length).toString();
      }

      return merged;
    } catch (err) {
      console.warn('[analytics] Failed to merge local sessions', err);
      return data;
    }
  };

  // Generate demo data for development/demo purposes
  const generateDemoData = () => {
    const demoData = {
      summary: {
        totalVisitors: 42,
        totalSessions: 67,
        totalPageViews: 156,
        totalEvents: 890,
        lastUpdated: new Date().toISOString()
      },
      recentSessions: [
        JSON.stringify({
          sessionId: 'demo-1',
          userId: 'user-1',
          startTime: Date.now() - 300000,
          locationInfo: { city: 'San Francisco', country: 'United States' },
          deviceInfo: { deviceType: 'desktop', browser: { browser: 'Chrome' } },
          summary: 'San Francisco - desktop'
        }),
        JSON.stringify({
          sessionId: 'demo-2',
          userId: 'user-2',
          startTime: Date.now() - 600000,
          locationInfo: { city: 'New York', country: 'United States' },
          deviceInfo: { deviceType: 'mobile', browser: { browser: 'Safari' } },
          summary: 'New York - mobile'
        }),
        JSON.stringify({
          sessionId: 'demo-3',
          userId: 'user-3',
          startTime: Date.now() - 900000,
          locationInfo: { city: 'London', country: 'United Kingdom' },
          deviceInfo: { deviceType: 'desktop', browser: { browser: 'Firefox' } },
          summary: 'London - desktop'
        }),
        JSON.stringify({
          sessionId: 'demo-4',
          userId: 'user-4',
          startTime: Date.now() - 1200000,
          locationInfo: { city: 'Toronto', country: 'Canada' },
          deviceInfo: { deviceType: 'tablet', browser: { browser: 'Safari' } },
          summary: 'Toronto - tablet'
        }),
        JSON.stringify({
          sessionId: 'demo-5',
          userId: 'user-5',
          startTime: Date.now() - 1500000,
          locationInfo: { city: 'Berlin', country: 'Germany' },
          deviceInfo: { deviceType: 'desktop', browser: { browser: 'Edge' } },
          summary: 'Berlin - desktop'
        })
      ],
      stats: {
        locations: {
          'United States': '25',
          'United Kingdom': '8',
          'Canada': '5',
          'Germany': '4',
          'France': '3'
        },
        devices: {
          'desktop': '28',
          'mobile': '12',
          'tablet': '2'
        },
        browsers: {
          'Chrome': '20',
          'Safari': '10',
          'Firefox': '8',
          'Edge': '4'
        }
      }
    };
    
    console.log('🎭 Demo data structure:', demoData);
    return demoData;
  };

  if (loading && !historicalData) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { summary, recentSessions, stats } = historicalData || {};

  // Parse recent sessions with better error handling
  const parsedSessions = recentSessions?.map((session, index) => {
    try {
      const parsed = JSON.parse(session);
      console.log(`✅ Parsed session ${index}:`, parsed);
      return parsed;
    } catch (parseError) {
      console.error(`❌ Failed to parse session ${index}:`, session, parseError);
      return null;
    }
  }).filter(Boolean) || [];

  console.log('📋 Final parsed sessions:', parsedSessions);

  // Create chart data
  const locationEntries = Object.entries(stats?.locations || {}).sort((a, b) => parseInt(b[1]) - parseInt(a[1]));
  const deviceEntries = Object.entries(stats?.devices || {}).sort((a, b) => parseInt(b[1]) - parseInt(a[1]));
  const browserEntries = Object.entries(stats?.browsers || {}).sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

  const topLocationCount = parseInt(locationEntries?.[0]?.[1] ?? '1');
  const topBrowserCount  = parseInt(browserEntries?.[0]?.[1] ?? '1');
  const safeTopLocationCount = topLocationCount === 0 ? 1 : topLocationCount;
  const safeTopBrowserCount  = topBrowserCount  === 0 ? 1 : topBrowserCount;

  return (
    <div className="p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Historical Analytics Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive visitor insights across all sessions
            </p>
          </div>
          <button
            onClick={fetchHistoricalData}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
        
        {error && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">⚠️ {error}</p>
          </div>
        )}
        
        {summary?.lastUpdated && (
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {new Date(summary.lastUpdated).toLocaleString()}
          </p>
        )}

        {/* Debug info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              🔧 Debug: Found {recentSessions?.length || 0} raw sessions, parsed {parsedSessions.length} successfully
            </p>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Visitors"
          value={summary?.totalVisitors || 0}
          icon="👥"
          color="blue"
          subtitle="Unique users"
        />
        <StatCard
          title="Total Sessions"
          value={summary?.totalSessions || 0}
          icon="🎯"
          color="green"
          subtitle="All visits"
        />
        <StatCard
          title="Page Views"
          value={summary?.totalPageViews || 0}
          icon="📄"
          color="purple"
          subtitle="Total views"
        />
        <StatCard
          title="Total Events"
          value={summary?.totalEvents || 0}
          icon="⚡"
          color="orange"
          subtitle="All interactions"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
        
        {/* Geographic Distribution */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-green-600">
            🌍 Geographic Distribution
          </h2>
          <div className="space-y-3">
            {locationEntries.slice(0, 8).map(([location, count]) => (
              <div key={location} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{location}</span>
                <div className="flex items-center gap-2">
                  <div
                    className="bg-green-200 rounded-full h-2"
                    style={{
                      width: `${Math.max(20, (parseInt(count) / safeTopLocationCount) * 100)}px`
                    }}
                  ></div>
                  <span className="text-sm font-bold text-green-600">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Device Types */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-blue-600">
            📱 Device Types
          </h2>
          <div className="space-y-4">
            {deviceEntries.map(([device, count]) => {
              const totalSessions = parseInt(summary?.totalSessions ?? '0') || 1;
              const percentage = Math.round((parseInt(count) / totalSessions) * 100);
              return (
                <div key={device} className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium capitalize">{device}</span>
                    <span className="text-sm font-bold">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Browser Usage */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-purple-600">
            🌐 Browser Usage
          </h2>
          <div className="space-y-3">
            {browserEntries.map(([browser, count]) => (
              <div key={browser} className="flex justify-between items-center">
                <span className="text-sm font-medium">{browser}</span>
                <div className="flex items-center gap-2">
                  <div
                    className="bg-purple-200 rounded-full h-2"
                    style={{
                      width: `${Math.max(15, (parseInt(count) / safeTopBrowserCount) * 80)}px`
                    }}
                  ></div>
                  <span className="text-sm font-bold text-purple-600">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-orange-600">
          🕒 Recent Sessions ({parsedSessions.length})
        </h2>
        
        {parsedSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">No session data available yet</p>
            <p className="text-sm mt-2">
              {error ? 'Check the debug info above for details' : 'Visit your site to generate session data'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Time</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Location</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Device</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Browser</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                </tr>
              </thead>
              <tbody>
                {parsedSessions.slice(0, 20).map((session, index) => (
                  <tr key={session.sessionId || index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">
                      {new Date(session.startTime).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div>
                        <div className="font-medium">{session.locationInfo?.city || 'Unknown'}</div>
                        <div className="text-gray-500 text-xs">{session.locationInfo?.country}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm capitalize">
                      {session.deviceInfo?.deviceType || 'Unknown'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {session.deviceInfo?.browser?.browser || 'Unknown'}
                    </td>
                    <td className="py-3 px-4 text-sm font-mono">
                      {session.userId?.substring(0, 8)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  const numericValue = Number(value) || 0;

  return (
    <div className={`rounded-lg border p-6 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold">{numericValue.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
    </div>
  );
};

export default HistoricalAnalytics; 